<script setup lang="ts">
import { onBeforeUnmount, ref, watch } from 'vue'

// A field with a menu under it. The menu closes on a press outside, on Escape, and when Tab moves focus out.
const open = ref(false)
const wrapper = ref<HTMLElement | null>(null)

// A press is watched, not only focus, because Safari does not focus a button that is clicked.
function closeOnPressOutside(event: MouseEvent) {
  const pressedInside = wrapper.value?.contains(event.target as Node) ?? false
  if (!pressedInside) open.value = false
}

function closeWhenFocusLeaves(event: FocusEvent) {
  const next = event.relatedTarget as Node | null
  // No next element means focus went to the page itself, which a click on a menu row can do. The press
  // handler covers a click outside, so the menu stays open here.
  if (next && !wrapper.value?.contains(next)) open.value = false
}

// Escape is stopped only while the menu is open, so a closed menu leaves Escape to a surrounding panel.
function closeOnEscape(event: KeyboardEvent) {
  if (!open.value) return
  event.stopPropagation()
  open.value = false
}

watch(open, (isOpen) => {
  if (isOpen) document.addEventListener('mousedown', closeOnPressOutside)
  else document.removeEventListener('mousedown', closeOnPressOutside)
})
onBeforeUnmount(() => document.removeEventListener('mousedown', closeOnPressOutside))
</script>

<template>
  <div ref="wrapper" class="dropdown" @focusout="closeWhenFocusLeaves" @keydown.esc="closeOnEscape">
    <slot :open="open" :show="() => (open = true)" :toggle="() => (open = !open)" />
    <div v-if="open" class="dropdown-menu show w-100 p-1 shadow-sm field-dropdown-menu" data-bs-popper="static">
      <slot name="menu" :close="() => (open = false)" />
    </div>
  </div>
</template>

<style scoped>
/* A deep sample tree must not grow without bound inside the form. */
.field-dropdown-menu {
  max-height: 18rem;
  overflow-y: auto;
}
</style>
